const CHAT_LINK_PENDING_KEY = 'chat_link_pending';
const CHAT_LINK_COMPLETION_RESULT_KEY = 'chat_link_completion_result';
const CHAT_LINK_LOCAL_TTL_MS = 30 * 60 * 1000;
const CHAT_LINK_RESULT_TTL_MS = 10 * 60 * 1000;

export type PendingChatLink = {
  linkId: string;
  state: string;
  capturedAt: number;
};

export type ChatLinkCompletionResult = {
  success: boolean;
  message: string;
  code?: number;
  completedAt: number;
};

export const isChatLinkRoute = (pathname?: string | null): boolean => pathname === '/chat/link';

export const readPendingChatLinkFromSearch = (search?: string | null): PendingChatLink | null => {
  const params = new URLSearchParams(search || '');
  const linkId = (params.get('link_id') || '').trim();
  const state = (params.get('state') || '').trim();

  if (!linkId || !state) {
    return null;
  }

  return {
    linkId,
    state,
    capturedAt: Date.now(),
  };
};

export const persistPendingChatLink = (pending: PendingChatLink | null): void => {
  if (!pending) return;

  try {
    sessionStorage.setItem(CHAT_LINK_PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Ignore storage errors; login still works without chat linking.
  }
};

export const capturePendingChatLinkFromSearch = (
  search?: string | null,
): PendingChatLink | null => {
  const pending = readPendingChatLinkFromSearch(search);
  persistPendingChatLink(pending);
  return pending;
};

export const getPendingChatLink = (): PendingChatLink | null => {
  try {
    const raw = sessionStorage.getItem(CHAT_LINK_PENDING_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const linkId = String((parsed as any).linkId || '').trim();
    const state = String((parsed as any).state || '').trim();
    const capturedAt = Number((parsed as any).capturedAt || 0);

    if (!linkId || !state) return null;
    if (
      Number.isFinite(capturedAt) &&
      capturedAt > 0 &&
      Date.now() - capturedAt > CHAT_LINK_LOCAL_TTL_MS
    ) {
      clearPendingChatLink();
      return null;
    }

    return {
      linkId,
      state,
      capturedAt: Number.isFinite(capturedAt) ? capturedAt : 0,
    };
  } catch {
    return null;
  }
};

export const clearPendingChatLink = (): void => {
  try {
    sessionStorage.removeItem(CHAT_LINK_PENDING_KEY);
  } catch {
    // Ignore storage errors.
  }
};

export const setChatLinkCompletionResult = (
  result: Omit<ChatLinkCompletionResult, 'completedAt'>,
): void => {
  try {
    const payload: ChatLinkCompletionResult = {
      ...result,
      completedAt: Date.now(),
    };
    sessionStorage.setItem(CHAT_LINK_COMPLETION_RESULT_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors.
  }
};

export const getChatLinkCompletionResult = (): ChatLinkCompletionResult | null => {
  try {
    const raw = sessionStorage.getItem(CHAT_LINK_COMPLETION_RESULT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const success = Boolean((parsed as any).success);
    const message = String((parsed as any).message || '').trim();
    const codeValue = Number((parsed as any).code ?? NaN);
    const completedAt = Number((parsed as any).completedAt || 0);

    if (!message) return null;
    if (
      Number.isFinite(completedAt) &&
      completedAt > 0 &&
      Date.now() - completedAt > CHAT_LINK_RESULT_TTL_MS
    ) {
      clearChatLinkCompletionResult();
      return null;
    }

    return {
      success,
      message,
      code: Number.isFinite(codeValue) ? codeValue : undefined,
      completedAt: Number.isFinite(completedAt) ? completedAt : 0,
    };
  } catch {
    return null;
  }
};

export const consumeChatLinkCompletionResult = (): ChatLinkCompletionResult | null => {
  const result = getChatLinkCompletionResult();
  clearChatLinkCompletionResult();
  return result;
};

export const clearChatLinkCompletionResult = (): void => {
  try {
    sessionStorage.removeItem(CHAT_LINK_COMPLETION_RESULT_KEY);
  } catch {
    // Ignore storage errors.
  }
};
